import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
export const colorsArray = (visualIdentity?: WorkspaceVisualIdentity) => [
  {
    id: 'accent',
    name: "Couleur d'accent",
    value: visualIdentity?.accentColor,
    color: visualIdentity?.accentColor,
  },
  {
    id: 'background',
    name: 'Couleur de fond',
    value: visualIdentity?.backgroundColor,
    color: visualIdentity?.backgroundColor,
  },
  {
    id: 'contrast',
    name: 'Couleur de contraste',
    value: visualIdentity?.contrastColor,
    color: visualIdentity?.contrastColor,
  },
  {
    id: 'gradient1-color1',
    name: 'Gradient 1',
    value: visualIdentity?.gradient1Color1,
    color: visualIdentity?.gradient1Color1,
  },

  {
    id: 'gradient2-color1',
    name: 'Gradient 2',
    value: visualIdentity?.gradient2Color1,
    color: visualIdentity?.gradient2Color1,
  },

  {
    id: 'secondary-background',
    name: 'Fond secondaire',
    value: visualIdentity?.secondaryBackgroundColor,
    color: visualIdentity?.secondaryBackgroundColor,
  },
  { id: 'black', name: 'Noir', value: '#000000', color: '#000000' },
  { id: 'white', name: 'Blanc', value: '#FFFFFF', color: '#FFFFFF' },
];
export const getBackgroundStyle = (
  backgroundColor: string,
  customBackgroundColor?: string,
  visualIdentity?: WorkspaceVisualIdentity,
) => {
  if (backgroundColor === 'accent' && visualIdentity) {
    return {
      background: visualIdentity.accentColor || '#2effb8',
    };
  }
  if (backgroundColor === 'background' && visualIdentity) {
    return {
      background: visualIdentity.backgroundColor || '#FFFFFF',
    };
  }
  if (backgroundColor === 'secondary-background' && visualIdentity) {
    return {
      background: visualIdentity.secondaryBackgroundColor || '#FFFFFF',
    };
  }
  if (backgroundColor === 'contrast' && visualIdentity) {
    return {
      background: visualIdentity.contrastColor || '#FFFFFF',
    };
  }

  if (backgroundColor === 'gradient1' || backgroundColor === 'gradient-1') {
    return {
      background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1}, ${visualIdentity?.gradient1Color2})`,
    };
  }
  if (backgroundColor === 'gradient2' || backgroundColor === 'gradient-2') {
    return {
      background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1} 0.2%, ${visualIdentity?.gradient2Color2} 104.63%)`,
    };
  }
  if (customBackgroundColor) {
    return { background: customBackgroundColor };
  }
  if (backgroundColor && backgroundColor.startsWith('#')) {
    return { background: backgroundColor };
  }
  return {};
};

export const getBorderStyle = (
  borderColor: string,
  customBorderColor?: string,
  visualIdentity?: WorkspaceVisualIdentity,
) => {
  if (
    borderColor === 'no-border' ||
    borderColor === 'transparent' ||
    borderColor === null ||
    !borderColor
  ) {
    return { border: 'none' };
  }
  if (borderColor && borderColor.startsWith('#')) {
    return { borderColor };
  }
  if (borderColor === 'accent' && visualIdentity) {
    return {
      borderColor: visualIdentity.accentColor || '#2effb8',
    };
  }
  if (borderColor === 'white') {
    return { borderColor: '#FFFFFF' };
  }
  if (borderColor === 'black') {
    return { borderColor: '#000000' };
  }
  if (customBorderColor) {
    return { borderColor: customBorderColor };
  }
  return {};
};

export const getFinalAccentColor = (
  accentColor: string,
  customAccentColor?: string,
  visualIdentity?: WorkspaceVisualIdentity,
) => {
  if (accentColor === 'accent' && visualIdentity) {
    return visualIdentity.accentColor || '#2effb8';
  }
  if (accentColor === 'contrast' && visualIdentity) {
    return visualIdentity.contrastColor || '#2C6CFF';
  }
  if (accentColor === 'background' && visualIdentity) {
    return visualIdentity.backgroundColor || '#FFFFFF';
  }
  if (accentColor === 'secondary-background' && visualIdentity) {
    return visualIdentity.secondaryBackgroundColor || '#FFFFFF';
  }
  if (customAccentColor) {
    return customAccentColor;
  }
  return accentColor;
};

// Renvoie noir ou blanc selon la couleur de fond fournie, à partir de sa luminance
// relative (formule WCAG). Permet de garder un texte de bouton lisible quelle que
// soit la couleur de fond, sans paramètre dédié.
// Seuil à 0.3 (et non le point d'égal contraste ~0.179) : on privilégie le texte
// blanc sur les fonds moyens/soutenus (ex. bleu de contraste #2c6cff, lum ≈ 0.19),
// où le blanc donne un rendu plus classique, tout en gardant du noir sur les fonds
// clairs à moyens-clairs (accent vert ≈ 0.76, périwinkle #95b6ff ≈ 0.47, blanc...).
export const getReadableTextColor = (backgroundColor?: string): string => {
  if (!backgroundColor || !/^#[0-9A-Fa-f]{6}$/.test(backgroundColor)) {
    return '#000000';
  }
  const toLinear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const r = toLinear(parseInt(backgroundColor.slice(1, 3), 16));
  const g = toLinear(parseInt(backgroundColor.slice(3, 5), 16));
  const b = toLinear(parseInt(backgroundColor.slice(5, 7), 16));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.3 ? '#000000' : '#FFFFFF';
};
const getColorValueById = (
  colorId: string,
  customColor?: string,
  visualIdentity?: WorkspaceVisualIdentity,
) => {
  if (colorId === 'no-border') {
    return 'transparent';
  }
  if (colorId === 'custom') {
    return customColor || '#FFFFFF';
  }
  if (colorId && /^#[0-9A-Fa-f]{6}$/.test(colorId)) {
    return colorId;
  }
  const color = colorsArray(visualIdentity)?.find((c) => c.id === colorId);
  return color?.color ?? color?.value ?? '#FFFFFF';
};
export const getFinalColor = (
  colorType: 'backgroundColor' | 'borderColor' | 'accentColor',
  type: any,
  visualIdentity?: WorkspaceVisualIdentity,
) => {
  const colorId = type[colorType];
  return getColorValueById(colorId || 'accent', undefined, visualIdentity);
};
